import { Component, EventEmitter, Input, Output, ContentChild, TemplateRef, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Table, TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';

export interface TableColumn {
  header: string;
  field: string;
  width?: string;
  headerColor?: string;
}

export interface TableSummaryItem {
  label: string;
  value: string;
}

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [CommonModule, TableModule, InputTextModule, TooltipModule, DatePickerModule, FormsModule],
  templateUrl: './data-table.component.html',
})
export class DataTableComponent implements OnChanges {
  @ContentChild('cellTemplate') cellTemplate?: TemplateRef<any>;
  @ContentChild('filtersTemplate') filtersTemplate?: TemplateRef<any>;
  @ContentChild('toolbarEndTemplate') toolbarEndTemplate?: TemplateRef<any>;
  @ContentChild('rowExpansionTemplate') rowExpansionTemplate?: TemplateRef<any>;
  @ViewChild('dt') table?: Table;
  @Input() columns: TableColumn[] = [];
  @Input() rows: any[] | null = null;
  @Input() highlightedColumn?: string;
  @Input() highlightColor?: string;
  @Input() highlightedRowField?: string;
  @Input() highlightedRowValue: unknown = true;
  @Input() statusField?: string;
  @Input() pageSize = 10;
  @Input() pageSizeOptions: number[] = [5, 10, 25, 50];
  @Input() showPageSizeSelector = false;
  @Input() showAllPageSizeOption = false;
  @Input() maxVisibleRows?: number;
  @Input() reserveEmptyRows = false;
  @Input() showPaginator = true;
  @Input() recordName = 'Records';
  @Input() globalFilterFields: string[] = [];
  @Input() filterFields: string[] = [];
  /** Filter fields that pick from a dropdown of the distinct values in the rows instead of free text. */
  @Input() optionFilterFields: string[] = [];
  @Input() sortableFields: string[] | null = null;
  @Input() searchPlaceholder = 'Search...';
  @Input() zebra = false;
  @Input() compact = false;
  @Input() fitColumns = false;
  @Input() tableMinWidth: string | null = null;
  @Input() rowActionEnabledField?: string;
  @Input() showRowActions = true;
  @Input() expansionControlInCell = false;
  @Input() showExport = false;
  @Input() showZeroBalanceToggle = false;
  @Input() zeroBalanceField = 'totalAmount';
  @Input() summaryValue: string | null = null;
  @Input() summaryLabel: string | null = null;
  @Input() summaryItems: TableSummaryItem[] = [];
  @Input() grandTotalValues: Record<string, string> | null = null;
  @Input() showColumnSelector = false;
  @Input() columnsReorderable = false;
  @Input() exportFileName = 'table-export';
  /**
   * 'page' exports just the rows currently on screen; 'filtered' exports every row
   * left after the active filters (search box, plus whatever the parent pre-filtered
   * into [rows]), ignoring pagination.
   */
  @Input() exportScope: 'page' | 'filtered' = 'page';
  @Input() dataStartDate: string | null = null;
  @Input() dataEndDate: string | null = null;
  @Input() selectionMode?: 'single' | 'multiple';
  @Input() selection: any | any[] = null;
  @Output() selectionChange = new EventEmitter<any | any[]>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() rowContextMenu = new EventEmitter<{ row: any; nativeEvent: MouseEvent }>();
  @Output() pageSizeSelectionChange = new EventEmitter<number | 'all'>();
  @Output() filteredRecordCountChange = new EventEmitter<number>();


  readonly skeletonRows = [1, 2, 3, 4, 5, 6];

  first = 0;
  searchTerm = '';
  fieldSearchTerm = '';
  fieldSearchDate: Date | null = null;
  activeFilterField = '';
  hiddenColumnFields = new Set<string>();
  private columnOrder: string[] = [];
  pageSizeSelection: number | 'all' | null = null;
  expandedRow: any | null = null;
  includeZeroBalanceOrActivity = false;

  get displayedRows(): any[] {
    const rows = this.rows ?? [];
    if (!this.showZeroBalanceToggle || this.includeZeroBalanceOrActivity) return rows;

    return rows.filter(row => !this.isZeroBalance(row?.[this.zeroBalanceField]));
  }

  get filteredRecordCount(): number {
    return (this.table?.filteredValue as any[] | null)?.length ?? this.displayedRows.length;
  }

  onTableFilter(event: { filteredValue?: unknown[] | null }): void {
    this.filteredRecordCountChange.emit(event.filteredValue?.length ?? this.displayedRows.length);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const rowsChange = changes['rows'];
    if (!rowsChange || rowsChange.firstChange) return;

    const previousLength = (rowsChange.previousValue as any[] | null)?.length ?? 0;
    const currentLength = (rowsChange.currentValue as any[] | null)?.length ?? 0;
    if (previousLength !== currentLength) this.resetToFirstPage();
  }

  get hasActionColumn(): boolean {
    return (!!this.rowExpansionTemplate && !this.expansionControlInCell)
      || (this.rowClick.observed && this.showRowActions);
  }

  readonly toggleExpansionFromCell = (event: Event, row: any): void => this.toggleRowExpansion(event, row);
  readonly isExpandedFromCell = (row: any): boolean => this.isRowExpanded(row);

  toggleRowExpansion(event: Event, row: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.expandedRow = this.expandedRow === row ? null : row;
  }

  isRowExpanded(row: any): boolean {
    return this.expandedRow === row;
  }

  get showToolbar(): boolean {
    return this.globalFilterFields.length > 0 || this.filterFields.length > 0 || !!this.filtersTemplate || !!this.toolbarEndTemplate ||
      this.showExport || !!this.dataPeriodLabel;
  }

  get dataPeriodLabel(): string | null {
    if (!this.dataStartDate && !this.dataEndDate) return null;
    const start = this.formatPeriodDate(this.dataStartDate);
    const end = this.formatPeriodDate(this.dataEndDate);
    if (start && end) return `${start} – ${end}`;
    return start || end;
  }

  private formatPeriodDate(value: string | null): string | null {
    if (!value) return null;
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return value;
    return `${match[3]}/${match[2]}/${match[1]}`;
  }

  get highlightBg(): string | null {
    if (!this.highlightColor) return null;
    const hex = this.highlightColor.replace('#', '');
    const normalized = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
    const value = parseInt(normalized, 16);
    const r = (value >> 16) & 255;
    const g = (value >> 8) & 255;
    const b = value & 255;
    return `rgba(${r}, ${g}, ${b}, 0.14)`;
  }

  get selectedFilterField(): string {
    return this.filterFields.includes(this.activeFilterField) ? this.activeFilterField : (this.filterFields[0] ?? '');
  }

  filterFieldLabel(field: string): string {
    return this.columns.find(column => column.field === field)?.header ?? field;
  }

  isOptionFilterField(field: string): boolean {
    return this.optionFilterFields.includes(field);
  }

  filterOptionValues(field: string): string[] {
    const values = new Set<string>();
    for (const row of this.displayedRows) {
      const value = row?.[field];
      if (value !== null && value !== undefined && String(value).trim() !== '') values.add(String(value));
    }
    return [...values].sort((a, b) => a.localeCompare(b));
  }

  selectFilterField(field: string): void {
    if (field === this.selectedFilterField) return;
    // A typed term and a dropdown choice are not interchangeable, so don't carry one over to the other.
    if (this.isOptionFilterField(field) || this.isOptionFilterField(this.selectedFilterField)) {
      this.fieldSearchTerm = '';
    }
    this.activeFilterField = field;
    this.fieldSearchDate = null;
    this.resetToFirstPage();
    this.table?.clear();
    if (this.fieldSearchTerm) this.table?.filter(this.fieldSearchTerm, field, 'contains');
    this.resetToFirstPage();
  }

  onFieldSearch(value: string): void {
    this.fieldSearchTerm = value;
    this.resetToFirstPage();
    this.table?.filter(
      value,
      this.selectedFilterField,
      this.isOptionFilterField(this.selectedFilterField) ? 'equals' : 'contains'
    );
    this.resetToFirstPage();
  }

  clearFieldSearch(): void {
    this.fieldSearchDate = null;
    this.onFieldSearch('');
  }

  onDateFieldSearch(value: Date | null): void {
    this.fieldSearchDate = value;
    if (!value || Number.isNaN(value.getTime())) {
      this.onFieldSearch('');
      return;
    }

    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const year = value.getFullYear();
    this.fieldSearchTerm = `${day}/${month}/${year}`;
    this.resetToFirstPage();
    this.table?.filter(`${year}-${month}-${day}`, this.selectedFilterField, 'contains');
    this.resetToFirstPage();
  }

  onFieldSearchPaste(event: ClipboardEvent): void {
    this.normalizePastedFilter(event, value => this.onFieldSearch(value));
  }

  exportExcel(): void {
    const availableRows = (this.table?.filteredValue as any[] | null) ?? this.displayedRows;
    const rows = this.showPaginator && this.exportScope === 'page'
      ? availableRows.slice(this.pageFirst, this.pageFirst + this.effectivePageSize)
      : availableRows;
    const escapeXml = (value: unknown): string => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
    const cell = (value: unknown, header = false): string => {
      const isNumber = typeof value === 'number' && Number.isFinite(value);
      const type = isNumber ? 'Number' : 'String';
      const content = header ? String(value ?? '') : (isNumber ? value : this.exportDisplayValue(value));
      const style = header ? ' ss:StyleID="Header"' : '';
      return `<Cell${style}><Data ss:Type="${type}">${escapeXml(content)}</Data></Cell>`;
    };
    const headerRow = `<Row>${this.visibleColumns.map(column => cell(column.header, true)).join('')}</Row>`;
    const dataRows = rows.map(row =>
      `<Row>${this.visibleColumns.map(column => cell(row?.[column.field])).join('')}</Row>`
    ).join('');
    const grandTotalRow = this.grandTotalValues
      ? `<Row ss:StyleID="GrandTotal">${this.visibleColumns
        .map(column => cell(this.grandTotalValues?.[column.field] ?? ''))
        .join('')}</Row>`
      : '';
    const workbook = `<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?>` +
      `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">` +
      `<Styles><Style ss:ID="Header"><Font ss:Bold="1"/><Interior ss:Color="#DCE6F1" ss:Pattern="Solid"/></Style>` +
      `<Style ss:ID="GrandTotal"><Font ss:Bold="1"/><Interior ss:Color="#E8F3FF" ss:Pattern="Solid"/></Style></Styles>` +
      `<Worksheet ss:Name="Table"><Table>${headerRow}${dataRows}${grandTotalRow}</Table></Worksheet></Workbook>`;
    const blob = new Blob([workbook], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.safeExportFileName}.xls`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  get visibleColumns(): TableColumn[] {
    return this.orderedColumns.filter(column => !this.hiddenColumnFields.has(column.field));
  }

  get orderedColumns(): TableColumn[] {
    // Non-reorderable tables must always follow the order supplied by their
    // parent. This also restores temporarily hidden columns to their canonical
    // position when a chart filter is cleared.
    if (!this.columnsReorderable) return this.columns;

    const availableFields = new Set(this.columns.map(column => column.field));
    this.columnOrder = this.columnOrder.filter(field => availableFields.has(field));
    for (const column of this.columns) {
      if (!this.columnOrder.includes(column.field)) this.columnOrder.push(column.field);
    }
    const byField = new Map(this.columns.map(column => [column.field, column]));
    return this.columnOrder
      .map(field => byField.get(field))
      .filter((column): column is TableColumn => !!column);
  }

  isColumnVisible(field: string): boolean {
    return !this.hiddenColumnFields.has(field);
  }

  toggleColumn(field: string): void {
    if (this.hiddenColumnFields.has(field)) {
      this.hiddenColumnFields.delete(field);
      this.hiddenColumnFields = new Set(this.hiddenColumnFields);
      return;
    }
    if (this.visibleColumns.length <= 1) return;
    this.hiddenColumnFields.add(field);
    this.hiddenColumnFields = new Set(this.hiddenColumnFields);
  }

  onColumnReorder(event: { dragIndex?: number; dropIndex?: number }): void {
    const dragIndex = event.dragIndex;
    const dropIndex = event.dropIndex;
    if (dragIndex == null || dropIndex == null || dragIndex === dropIndex) return;
    const visible = [...this.visibleColumns];
    const [moved] = visible.splice(dragIndex, 1);
    if (!moved) return;
    visible.splice(dropIndex, 0, moved);
    const hidden = this.orderedColumns.filter(column => this.hiddenColumnFields.has(column.field));
    this.columnOrder = [...visible, ...hidden].map(column => column.field);
  }

  private exportDisplayValue(value: unknown): string {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return value.split('T')[0];
    return String(value ?? '');
  }

  private get safeExportFileName(): string {
    const safeName = this.exportFileName.trim().replace(/[<>:"/\\|?*]+/g, '-').replace(/\s+/g, ' ');
    return safeName || 'table-export';
  }

  isSortable(_field: string): boolean {
    return true;
  }

  get usesCustomSort(): boolean {
    return true;
  }

  sortRows(event: { data?: any[]; field?: string | string[]; order?: number | null }): void {
    const rows = event.data;
    const field = Array.isArray(event.field) ? event.field[0] : event.field;
    const order = event.order ?? 1;
    if (!rows || !field) return;

    rows.sort((left, right) => order * this.compareSortValues(left?.[field], right?.[field], field));
  }

  private compareSortValues(left: unknown, right: unknown, field: string): number {
    if (left == null && right == null) return 0;
    if (left == null) return -1;
    if (right == null) return 1;

    if (field === 'monthLabel') {
      const leftMonth = this.monthSortValue(left);
      const rightMonth = this.monthSortValue(right);
      if (leftMonth !== null && rightMonth !== null) return leftMonth - rightMonth;
    }

    if (typeof left === 'number' && typeof right === 'number') return left - right;

    const leftNumber = this.numericSortValue(left);
    const rightNumber = this.numericSortValue(right);
    if (leftNumber !== null && rightNumber !== null) return leftNumber - rightNumber;

    if (/date/i.test(field)) {
      const leftDate = Date.parse(String(left));
      const rightDate = Date.parse(String(right));
      if (Number.isFinite(leftDate) && Number.isFinite(rightDate)) return leftDate - rightDate;
    }

    return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
  }

  private numericSortValue(value: unknown): number | null {
    if (typeof value !== 'string') return null;

    const normalized = value.trim()
      .replace(/^\((.*)\)$/, '-$1')
      .replace(/[,\s£$€%]/g, '');
    if (!normalized || !/^[+-]?\d+(?:\.\d+)?$/.test(normalized)) return null;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private monthSortValue(value: unknown): number | null {
    const label = String(value).trim();
    const yearMonth = label.match(/^(\d{4})-(\d{1,2})(?:-\d{1,2})?$/);
    if (yearMonth) {
      const month = Number(yearMonth[2]);
      return month >= 1 && month <= 12 ? Number(yearMonth[1]) * 12 + month - 1 : null;
    }

    const namedMonth = label.match(/^([A-Za-z]+)[\s-]+(\d{2}|\d{4})$/);
    if (!namedMonth) return null;

    const monthNames = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december',
    ];
    const normalizedMonth = namedMonth[1].toLocaleLowerCase();
    const month = monthNames.findIndex(name => name.startsWith(normalizedMonth));
    if (month < 0) return null;

    const parsedYear = Number(namedMonth[2]);
    const year = namedMonth[2].length === 2 ? 2000 + parsedYear : parsedYear;
    return year * 12 + month;
  }

  isRowHighlighted(row: any): boolean {
    return !!this.highlightedRowField &&
      row?.[this.highlightedRowField] === this.highlightedRowValue;
  }

  onSearch(value: string): void {
    this.searchTerm = value;
    this.resetToFirstPage();
    this.table?.filterGlobal(value, 'contains');
    this.resetToFirstPage();
  }

  clearSearch(): void {
    this.onSearch('');
  }

  onSearchPaste(event: ClipboardEvent): void {
    this.normalizePastedFilter(event, value => this.onSearch(value));
  }

  private normalizePastedFilter(event: ClipboardEvent, apply: (value: string) => void): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;

    setTimeout(() => {
      const normalizedValue = input.value.trim();
      input.value = normalizedValue;
      apply(normalizedValue);
    });
  }

  get rowSlotCount(): number {
    return this.effectivePageSize;
  }

  get effectivePageSize(): number {
    if (this.pageSizeSelection === 'all') return Math.max(this.displayedRows.length, 1);
    return Math.max(this.pageSize || 1, 1);
  }

  get visibleRowSlotCount(): number {
    const maxVisibleRows = Math.max(this.maxVisibleRows ?? this.rowSlotCount, 1);
    return Math.min(this.rowSlotCount, maxVisibleRows);
  }

  get pageFirst(): number {
    const totalRows = this.displayedRows.length;
    if (totalRows === 0) return 0;
    return Math.min(this.first, Math.floor((totalRows - 1) / this.rowSlotCount) * this.rowSlotCount);
  }

  get tableHeight(): string {
    const rowSlots = this.reserveEmptyRows
      ? this.visibleRowSlotCount
      : Math.max(1, Math.min(this.visibleRowSlotCount, this.displayedRows.length - this.pageFirst));
    const grandTotalRowSlots = this.grandTotalValues ? 1 : 0;
    return `${46 + (rowSlots + grandTotalRowSlots) * 48}px`;
  }

  get placeholderRows(): number[] {
    if (!this.reserveEmptyRows) return [];
    const totalRows = this.displayedRows.length;
    if (totalRows === 0) return [];

    const visibleRows = Math.min(this.visibleRowSlotCount, Math.max(totalRows - this.pageFirst, 0));
    const missingRows = Math.max(this.visibleRowSlotCount - visibleRows, 0);
    return Array.from({ length: missingRows }, (_, index) => index);
  }

  onPage(event: { first?: number }): void {
    this.first = event.first ?? 0;
  }

  private resetToFirstPage(): void {
    this.first = 0;
    if (this.table) this.table.first = 0;
  }

  onRowContextMenu(event: MouseEvent, row: any): void {
    event.preventDefault();
    this.rowContextMenu.emit({ row, nativeEvent: event });
  }

  onPageSizeChange(newSize: any): void {
    if (newSize === 'all') {
      this.pageSizeSelection = 'all';
      this.pageSize = Math.max(this.displayedRows.length, 1);
      this.first = 0;
      this.pageSizeSelectionChange.emit('all');
      return;
    }

    const parsedSize = Number(newSize);
    if (!Number.isFinite(parsedSize) || parsedSize < 1) return;

    this.pageSizeSelection = parsedSize;
    this.pageSize = parsedSize;
    this.first = 0;
    this.pageSizeSelectionChange.emit(parsedSize);
  }

  onIncludeZeroBalanceChange(include: boolean): void {
    this.includeZeroBalanceOrActivity = include;
    this.resetToFirstPage();
  }

  private isZeroBalance(value: unknown): boolean {
    if (typeof value === 'number') return Number.isFinite(value) && value === 0;
    if (typeof value !== 'string') return false;

    const normalized = value.trim()
      .replace(/^\((.*)\)$/, '-$1')
      .replace(/[,\s£$€]/g, '');
    if (!normalized) return false;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) && parsed === 0;
  }

  onRowActionPointerDown(event: PointerEvent, row: any): void {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.rowClick.emit(row);
  }

  onRowActionClick(event: MouseEvent, row: any): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.detail === 0) this.rowClick.emit(row);
  }

  isRowActionEnabled(row: any): boolean {
    return !this.rowActionEnabledField || row?.[this.rowActionEnabledField] === true;
  }

  isDateColumn(field: string): boolean {
    return ['date', 'startDate', 'endDate', 'invoiceDate', 'dueDate', 'firstWorkDate', 'lastWorkDate', 'transactionDate', 'bankTransactionDate'].includes(field);
  }

  isCompactTextColumn(field: string): boolean {
    return ['description', 'reference', 'invoiceDetails'].includes(field);
  }

  isCompactIdentifierColumn(field: string): boolean {
    return ['invoiceNumber', 'invoiceStatus'].includes(field);
  }

  isCompactAmountColumn(field: string): boolean {
    return ['amount', 'amountPaid'].includes(field);
  }

  isContentWidthColumn(field: string): boolean {
    return field === 'source';
  }

  displayValue(row: any, field: string): string {
    const value = row?.[field];
    if (value === null || value === undefined || value === '') return '-';

    if (this.isDateColumn(field) && typeof value === 'string') {
      const isoDate = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoDate) return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;

      const slashDate = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (slashDate) {
        return `${slashDate[1].padStart(2, '0')}/${slashDate[2].padStart(2, '0')}/${slashDate[3]}`;
      }
    }

    if (this.isCurrencyField(field) && typeof value === 'number') {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }

    if ((field === 'dso' || field === 'debtorDays' || field === 'countBackDSO') && typeof value === 'number') {
      return `${value.toFixed(2)}d`;
    }

    if (field === 'daysBasis' && typeof value === 'number') {
      return value.toFixed(2);
    }

    if (
      (field === 'avgMargin' ||
        field === 'gpPct' ||
        field === 'revenueConcentrationPercent' ||
        field === 'monthlyCashCollectedPercent' ||
        field === 'cumulativeCashCollectedPercent' ||
        field === 'agedDebtOver90Percent' ||
        field === 'aged90Percent' ||
        field === 'percentage' ||
        field === 'kpiPercent') &&
      typeof value === 'number'
    ) {
      return `${value.toFixed(1)}%`;
    }

    return String(value);
  }

  isNumericEmphasisField(field: string): boolean {
    return field === 'total' ||
      field === 'amount' ||
      field === 'amountDue' ||
      this.isCurrencyField(field) ||
      field === 'dso' ||
      field === 'debtorDays' ||
      field === 'daysBasis' ||
      field === 'countBackDSO' ||
      field === 'avgMargin' ||
      field === 'gpPct' ||
      field === 'rowNumber' ||
      field === 'outstandingInvoiceCount' ||
      field === 'agedInvoiceCountOver90Days' ||
      field === 'daysOverdue' ||
      field === 'invoiceAgeDays' ||
      field === 'revenueInvoiceCount' ||
      field === 'totalClients' ||
      field === 'placements' ||
      field === 'timesheets' ||
      field === 'contractors';
  }

  private isCurrencyField(field: string): boolean {
    return field === 'amount' ||
      field === 'totalOutstanding' ||
      field === 'bucket0To30' ||
      field === 'bucket31To60' ||
      field === 'bucket61To90' ||
      field === 'bucket90Plus' ||
      field === 'billedLedgerAmount' ||
      field === 'permanentRevenue' ||
      field === 'temporaryRevenue' ||
      field === 'revenue' ||
      field === 'grossProfit' ||
      field === 'commissionPaid' ||
      field === 'outstandingBalance' ||
      field === 'monthlyRevenue' ||
      field === 'cumulativeRevenue' ||
      field === 'outstandingReceivablesAtMonthEnd' ||
      field === 'monthlyCashCollected' ||
      field === 'cumulativeCashCollected' ||
      field === 'totalOutstandingAtMonthEnd' ||
      field === 'agedDebtOver90Days' ||
      field === 'agedDebt90Plus' ||
      field === 'cashCollection' ||
      field === 'turnover' ||
      field === 'salesLedgerTotal' ||
      field === 'invoiceTotal' ||
      field === 'outstandingAtMonthEnd' ||
      field === 'amountPaid' ||
      field === 'creditNotesApplied' ||
      field === 'overpaymentsApplied' ||
      field === 'prepaymentsApplied' ||
      field === 'outstandingAmount' ||
      field === 'originalReceiptAmount' ||
      field === 'allocatedAmount' ||
      field === 'refundedAmount' ||
      field === 'appliedOrRefundedAmount' ||
      field === 'unallocatedCashAmount' ||
      field === 'bankTransactionTotal' ||
      field === 'kpiValue' ||
      field === 'value' ||
      field === 'totalAmount';
  }

  statusBadge(value: string): string {
    const v = (value ?? '').toLowerCase();
    if (v === 'overdue')                  return 'badge badge-negative';
    if (v === 'pending')                  return 'badge badge-warning';
    if (v === 'approved' || v === 'paid') return 'badge badge-positive';
    if (v === 'authorised')               return 'badge badge-warning';
    if (v === 'draft')                    return 'badge badge-neutral';
    return 'badge badge-neutral';
  }
}
